import { api, unwrap, type APIResponse } from "./client";

/**
 * The buyers an operator's agents can warm-transfer a customer to. Mirrors
 * app/routers/transfer_destination.py's TransferDestinationOut.
 *
 * `routing` is the wire value ("pstn" | "sip") the backend stores and later
 * uses to pick a dial-string builder — it is never shown to an operator
 * verbatim. The page renders it as "Phone number" / "SIP address".
 */
export interface TransferDestination {
  transfer_destination_id: number;
  name: string;
  target: string;
  routing: "pstn" | "sip";
  caller_id: string | null;
  is_active: boolean;
  sort_order: number;
  // Which of this operator's agents may use it. Empty means every agent may —
  // see app/services/transfer_destinations.py::list_for_agent.
  allowed_agent_ids: number[];
}

export interface TransferDestinationInput {
  name: string;
  target: string;
  routing: "pstn" | "sip";
  caller_id?: string | null;
  sort_order?: number;
  allowed_agent_ids?: number[];
}

// Not /api/destinations -- that prefix is the existing rate-table
// destination-prefix CRUD (app/routers/destination.py) and is a different
// resource entirely.
const BASE = "/transfer-destinations";

export async function listTransferDestinations(): Promise<TransferDestination[]> {
  const resp = await api.get<APIResponse<TransferDestination[]>>(`${BASE}/`);
  return unwrap(resp.data);
}

export async function createTransferDestination(
  body: TransferDestinationInput,
): Promise<TransferDestination> {
  const resp = await api.post<APIResponse<TransferDestination>>(`${BASE}/`, body);
  return unwrap(resp.data);
}

export async function updateTransferDestination(
  id: number,
  body: Partial<TransferDestinationInput> & { is_active?: boolean },
): Promise<TransferDestination> {
  const resp = await api.patch<APIResponse<TransferDestination>>(`${BASE}/${id}`, body);
  return unwrap(resp.data);
}

export async function deleteTransferDestination(id: number): Promise<void> {
  const resp = await api.delete<APIResponse<{ deleted: boolean }>>(`${BASE}/${id}`);
  unwrap(resp.data);
}

/**
 * The AGENT-side view for a later phase (Task 3.1's GET /agent/transfer-
 * destinations): only what an agent may actually press — active, and either
 * unrestricted or explicitly granted to them. Not called from this screen;
 * exported now because it belongs next to the shape it returns.
 */
export async function listAgentTransferDestinations(): Promise<TransferDestination[]> {
  const resp = await api.get<APIResponse<TransferDestination[]>>(
    "/agent/transfer-destinations",
  );
  return unwrap(resp.data);
}

/**
 * Shape-generic over FastAPI's {detail}/{error} bodies -- same job as
 * extensions.ts's agentSeatErrorMessage, kept local rather than imported so
 * this module has no reason to reach into an unrelated feature's API file.
 * The router (app/routers/transfer_destination.py) deliberately never echoes
 * a service exception's raw text for anything that could carry an id, so
 * `detail` here is always operator-safe to render verbatim -- never a raw
 * "Request failed with status code 409".
 */
export function transferDestinationErrorMessage(err: unknown): string {
  const ax = err as {
    response?: { data?: { detail?: string; error?: string } };
    message?: string;
  };
  return (
    ax?.response?.data?.detail ||
    ax?.response?.data?.error ||
    ax?.message ||
    "Something went wrong. Please try again."
  );
}
